/** Suntronix ONE — Service Catalogue Home
 *  Customer-first, outcome-driven service picker.
 *
 *  Layout:
 *   - "How can we help?" header
 *   - Universal search
 *   - Featured quick actions (from /services/featured)
 *   - 11 outcome group tiles (from /services/groups)
 *   - Recently used strip (localStorage)
 *   - "Manage catalogue" link (Suntronix internal only)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConciergeWidget from "@/components/app/ConciergeWidget";
import MyRequestsStrip from "@/components/app/MyRequestsStrip";
import { allowedGroupsForUser, ALL_GROUPS } from "@/lib/roleGroups";
import {
  ShoppingBag, Rocket, ArrowRight, Move, LifeBuoy, Warehouse, Undo2,
  RefreshCw, Recycle, ClipboardCheck, LineChart, Sparkles,
  Search, Settings2, Star, BarChart3, Layers, Eye,
} from "lucide-react";

const PLAYTEST_ROLES = [
  { key: "self", label: "Your role" },
  { key: "customer_hr", label: "HR" },
  { key: "customer_it", label: "IT" },
  { key: "customer_procurement", label: "Procurement" },
  { key: "customer_finance", label: "Finance" },
  { key: "employee_user", label: "Employee" },
  { key: "executive", label: "Executive" },
];

const PLAYTEST_KEY = "st-services-playtest-role";

const GROUP_ICON = {
  get_technology: ShoppingBag,
  deploy: Rocket,
  move: Move,
  support: LifeBuoy,
  store: Warehouse,
  recover: Undo2,
  reuse: RefreshCw,
  retire: Recycle,
  manage: ClipboardCheck,
  plan: LineChart,
  premium: Sparkles,
};

const RECENT_KEY = "st-services-recent";

function readRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); }
  catch { return []; }
}

export default function ServiceCatalogue() {
  const { user } = useAuth();
  const isSuntronix =
    !!user &&
    (!user.master_company_id ||
      user.role === "super_admin" ||
      String(user.role || "").startsWith("ops_") ||
      String(user.role || "").startsWith("suntronix_"));
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState(readRecent());

  // Suntronix-only "Playtest role" toggle — override role locally to
  // validate role-filtered outcomes without impersonation.
  const [playtestRole, setPlaytestRole] = useState(() => {
    try { return localStorage.getItem(PLAYTEST_KEY) || "self"; }
    catch { return "self"; }
  });
  useEffect(() => {
    try { localStorage.setItem(PLAYTEST_KEY, playtestRole); } catch { /* ignore */ }
  }, [playtestRole]);

  const allowedGroups = useMemo(() => {
    if (isSuntronix && playtestRole !== "self") {
      // Simulate a customer role — build a "fake" user so the mapping stays
      // in one place (`roleGroups.js`).
      return allowedGroupsForUser({ role: playtestRole, master_company_id: "playtest" });
    }
    return allowedGroupsForUser(user);
  }, [user, isSuntronix, playtestRole]);
  const visibleGroups = useMemo(
    () => groups.filter((g) => allowedGroups.includes(g.key)),
    [groups, allowedGroups],
  );

  useEffect(() => {
    Promise.all([
      api.get("/services/groups"),
      api.get("/services/featured", { params: { limit: 6 } }),
      api.get("/services/bundles"),
    ]).then(([g, f, b]) => {
      setGroups(g.data.items || []);
      setFeatured(f.data.items || []);
      setBundles(b.data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  // Search across the flat services list
  const [searchResults, setSearchResults] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      api.get("/services").then(({ data }) => {
        const needle = q.trim().toLowerCase();
        const items = (data.items || []).filter((s) =>
          (s.name || "").toLowerCase().includes(needle) ||
          (s.customer_facing_name || "").toLowerCase().includes(needle) ||
          (s.short_description || "").toLowerCase().includes(needle) ||
          (s.customer_description || "").toLowerCase().includes(needle) ||
          (s.code || "").toLowerCase().includes(needle)
        );
        setSearchResults(items.slice(0, 20));
      });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const openService = (svc) => {
    const next = [svc.code, ...recent.filter((c) => c !== svc.code)].slice(0, 6);
    setRecent(next);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    navigate(`/services/detail/${svc.code}`);
  };

  const recentServices = useMemo(() => {
    const byCode = new Map();
    groups.forEach((g) => (g.services || []).forEach?.((s) => byCode.set(s.code, s)));
    return recent.map((c) => byCode.get(c)).filter(Boolean).slice(0, 6);
  }, [recent, groups]);

  return (
    <div className="space-y-8 max-w-[1500px]">
      {/* Hero */}
      <div className="pb-2">
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-[color:var(--st-text-muted)]">Services</div>
        <h1 className="mt-1 font-heading text-[36px] leading-[1.1] tracking-[-0.01em] font-medium text-[color:var(--st-text)]">
          How can we help?
        </h1>
        <p className="mt-2 text-[15px] text-[color:var(--st-text-muted)] max-w-2xl">
          Choose what you need. ONE will guide you through the rest — no forms buried in operations, no internal jargon.
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-2xl" data-testid="services-search-wrap">
        <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" strokeWidth={1.5} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="What would you like to do? — e.g. new employee laptop, recover MacBook, refresh 50 devices"
          className="w-full h-12 pl-10 pr-4 rounded-md border border-slate-200 bg-white text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/5"
          data-testid="services-search-input"
        />
        {searchResults.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-md shadow-lg max-h-96 overflow-y-auto">
            {searchResults.map((s) => (
              <button
                key={s.code}
                onClick={() => openService(s)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                data-testid={`services-search-hit-${s.code}`}
              >
                <div className="text-sm font-medium text-slate-900">{s.customer_facing_name || s.name}</div>
                {s.short_description && <div className="text-xs text-slate-500 mt-0.5">{s.short_description}</div>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Concierge — natural language → suggested service */}
      <ConciergeWidget />

      {/* My recent requests */}
      <MyRequestsStrip />

      {/* Featured / Quick actions */}
      {featured.length > 0 && (
        <section>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-[color:var(--st-text-muted)] mb-2">
            Quick actions
          </div>
          <div className="flex flex-wrap gap-2">
            {featured.map((s) => (
              <button
                key={s.code}
                onClick={() => openService(s)}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-slate-200 bg-white text-sm text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                data-testid={`services-featured-${s.code}`}
              >
                <Star className="h-3.5 w-3.5 text-[color:var(--st-accent)]" strokeWidth={1.6} />
                {s.customer_facing_name || s.name}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Recently used */}
      {recentServices.length > 0 && (
        <section>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-[color:var(--st-text-muted)] mb-2">
            Recently used
          </div>
          <div className="flex flex-wrap gap-2">
            {recentServices.map((s) => (
              <button
                key={s.code}
                onClick={() => openService(s)}
                className="inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs text-slate-600 border border-slate-200 bg-white hover:bg-slate-50"
                data-testid={`services-recent-${s.code}`}
              >
                {s.customer_facing_name || s.name}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Suntronix-only: preview the outcome view as a customer role */}
      {isSuntronix && (
        <div className="flex flex-wrap items-center gap-2" data-testid="playtest-bar">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            <Eye className="h-3.5 w-3.5" strokeWidth={1.6} /> Preview as
          </span>
          <div className="flex flex-wrap gap-1.5">
            {PLAYTEST_ROLES.map((r) => (
              <button
                key={r.key}
                onClick={() => setPlaytestRole(r.key)}
                className={
                  "text-[11px] font-medium h-7 px-2.5 rounded-full border transition-colors " +
                  (playtestRole === r.key
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300")
                }
                data-testid={`playtest-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {playtestRole !== "self" && (
            <span className="ml-1 text-[11px] text-[color:var(--st-accent)]">Showing {visibleGroups.length} of {ALL_GROUPS.length} outcomes</span>
          )}
        </div>
      )}

      {/* Outcome groups */}
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-heading text-[15px] font-semibold text-slate-900">Choose an outcome</h2>
          {isSuntronix && (
            <div className="flex items-center gap-2">
              <Link
                to="/admin/services/analytics"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium text-slate-700 border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                data-testid="services-analytics-btn"
              >
                <BarChart3 className="h-3.5 w-3.5" strokeWidth={1.6} /> Analytics
              </Link>
              <Link
                to="/admin/masters/service-catalogue"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium text-slate-700 border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                data-testid="services-manage-btn"
              >
                <Settings2 className="h-3.5 w-3.5" strokeWidth={1.6} /> Manage catalogue
              </Link>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="st-skeleton h-32 rounded-md" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {visibleGroups.map((g) => {
              const Icon = GROUP_ICON[g.key] || ShoppingBag;
              return (
                <Link
                  key={g.key}
                  to={`/services/group/${g.key}`}
                  className="group text-left bg-white border border-slate-200 rounded-md p-5 hover:-translate-y-px hover:shadow-sm hover:border-slate-300 transition-[transform,box-shadow,border-color]"
                  data-testid={`services-group-${g.key}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="h-10 w-10 rounded-md bg-slate-900 text-amber-400 grid place-items-center">
                      <Icon className="h-4 w-4" strokeWidth={1.6} />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">{g.count} services</div>
                  </div>
                  <div className="mt-3 font-heading text-lg font-semibold text-slate-900">{g.label}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-relaxed">{g.hint}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 group-hover:text-slate-900">
                    Explore <ArrowRight className="h-3 w-3" strokeWidth={1.6} />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Bundles — outcome-shaped bundles that span multiple services */}
      {bundles.length > 0 && (
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-heading text-[15px] font-semibold text-slate-900">Outcome bundles</h2>
            <span className="text-[11px] text-slate-500">One request. One outcome. Multiple services.</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {bundles.map((b) => (
              <Link
                key={b.code}
                to={`/services/bundle/${b.code}`}
                className="group text-left bg-white border border-slate-200 rounded-md p-5 hover:-translate-y-px hover:shadow-sm hover:border-slate-300 transition-[transform,box-shadow,border-color]"
                data-testid={`services-bundle-${b.code}`}
              >
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-md bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)] grid place-items-center">
                    <Layers className="h-4 w-4" strokeWidth={1.6} />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{(b.included_services || []).length} services</div>
                </div>
                <div className="mt-3 font-heading text-base font-semibold text-slate-900">{b.customer_facing_name || b.name}</div>
                {b.description && <div className="text-xs text-slate-500 mt-1 leading-relaxed">{b.description}</div>}
                <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 group-hover:text-slate-900">
                  Open bundle <ArrowRight className="h-3 w-3" strokeWidth={1.6} />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
